<template>
  <div ref="view" class="c-cards" :class="{ 'c-visible': viewWidth > 0 }">
    <CardCarousel
      v-if="isCarousel"
      :items="items"
      :card-width="minCardWidth"
      :loading="loading"
      :width="viewWidth"
    >
      <template #card="props">
        <slot name="card" :item="props.item" />
      </template>
    </CardCarousel>

    <CardGrid
      v-else-if="isGrid"
      :infinite="true"
      :items="items"
      :loading="loading"
      :width="viewWidth"
    >
      <template #card="props">
        <DraggableNode
          :active="draggable"
          :drag-type="dragType"
          :identifier="`${props.item.id}`"
          @drop="$emit('move', $event)"
        >
          <slot name="card" :item="props.item" />
        </DraggableNode>
      </template>
    </CardGrid>

    <CardVirtualGrid
      v-else-if="isVirtualGrid"
      :items="items"
      :loading="loading"
      :min-card-height="minCardHeight"
      :width="viewWidth"
    >
      <template #card="props">
        <slot name="card" :item="props.item" />
      </template>
    </CardVirtualGrid>

    <CardTree
      v-else-if="isTree"
      class="mb-12"
      :attribute="treeAttribute"
      :expand="treeExpand"
      :items="items"
      :loading="loading"
      :width="viewWidth"
    >
      <template #card="props">
        <slot name="card" :item="props.item" />
      </template>
    </CardTree>
  </div>
</template>

<script>
import CardCarousel from '@/components/base/CardCarousel'
import CardGrid from '@/components/base/CardGrid'
import CardTree from '@/components/base/CardTree'
import CardVirtualGrid from '@/components/base/CardVirtualGrid'
import DraggableNode from '@/components/base/DraggableNode'
import { createResizeObserver } from '@/utilities/observer.js'

export default {
  name: 'CardView',

  components: {
    CardCarousel,
    CardGrid,
    CardTree,
    CardVirtualGrid,
    DraggableNode
  },

  props: {
    /* card entities */

    items: {
      type: Array,
      required: true
    },

    loading: {
      type: Boolean,
      required: false,
      default: false
    },

    maxItems: {
      type: Number,
      required: false,
      default: 0
    },

    /* card layout */

    layout: {
      type: String,
      required: false,
      default: 'grid'
    },

    width: {
      type: Number,
      required: false,
      default: 0
    },

    /* card dimensions */

    minCardHeight: {
      type: Number,
      required: false,
      default: 500
    },

    minCardWidth: {
      type: Number,
      required: false,
      default: 300
    },

    /* card grid */

    draggable: {
      type: Boolean,
      required: false,
      default: false
    },

    dragType: {
      type: String,
      required: false,
      default: 'generic'
    },

    /* card tree */

    treeAttribute: {
      type: String,
      required: false,
      default: 'topic'
    },

    treeExpand: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: function () {
    return {
      isActivated: false,
      viewWidth: this.width,
      resizeObserver: null
    }
  },

  computed: {
    isCarousel() {
      return this.layout === 'carousel'
    },

    isGrid() {
      return this.layout === 'grid' && this.optimizedGridLayout === 'grid'
    },

    isTree() {
      return this.layout === 'tree'
    },

    isVirtualGrid() {
      return this.layout === 'grid' && this.optimizedGridLayout === 'vgrid'
    },

    optimizedGridLayout() {
      return this.maxItems > 48 ? 'vgrid' : 'grid'
    }
  },

  watch: {
    viewWidth: {
      immediate: true,
      handler: function (_newValue, _oldValue) {
        // console.error('[CardView]: width=', newValue)
      }
    }
  },

  created: function () {
    this.resizeObserver = createResizeObserver(this.onResize)
  },

  mounted: function () {
    // initial viewWidth doesn't include:
    // 1)the left nav width (because it has a delayed transition)
    // 2)the scrollbar 15px width (Chrome/Edge only)
    this.viewWidth = this.$refs.view.clientWidth
    this.resizeObserver.observe(this.$refs.view)
  },

  activated: function () {
    this.isActivated = true
  },

  deactivated: function () {
    this.isActivated = false
  },

  beforeDestroy: function () {
    this.resizeObserver.disconnect()
  },

  methods: {
    onResize(width, _height) {
      this.viewWidth = width > 0 ? width : this.$refs.view.clientWidth
      // account for 2 x 12px of padding
      this.viewWidth = this.viewWidth > 0 ? this.viewWidth - 24 : 0
    }
  }
}
</script>

<style lang="scss" scoped>
.c-cards {
  visibility: hidden;
  &.c-visible {
    visibility: visible;
  }
}
</style>
